import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Home, ChevronDown, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { AuthModal } from './AuthModal';
import { TicketModal } from './ticket/TicketModal';
import { supabase } from '../lib/supabase';

interface HeaderProps {
  isAdmin?: boolean;
  isInsurer?: boolean;
  showDashboard?: boolean;
  showAdmin?: boolean;
  onToggleAdmin?: () => void;
  onToggleDashboard?: () => void;
}

export function Header({ 
  isAdmin, 
  isInsurer, 
  showAdmin, 
  showDashboard = false,
  onToggleAdmin, 
  onToggleDashboard = () => {},
}: HeaderProps) {
  const navigate = useNavigate();
  const { user, signOut, refreshAuth } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [showSupportModal, setShowSupportModal] = React.useState(false);
  const [openTickets, setOpenTickets] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Refresh auth state on mount
  useEffect(() => {
    refreshAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchOpenTickets();
    }
  }, [user]);


  const fetchOpenTickets = async () => {
    try {
      const { count } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress']);

      setOpenTickets(count || 0);
    } catch (error) {
      console.error('Error fetching open tickets:', error);
      setOpenTickets(0);
    }
  };

  const closeMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/" className="flex items-center group">
              <div className="h-10 w-10 md:h-8 md:w-8 flex items-end space-x-0.5 transform group-hover:scale-110 transition-transform">
                <div className="w-2 md:w-1.5 h-10 md:h-8 bg-purple-600 rounded-sm"></div>
                <div className="w-2 md:w-1.5 h-8 md:h-6 bg-blue-600 rounded-sm"></div>
                <div className="w-2 md:w-1.5 h-7 md:h-5 bg-yellow-400 rounded-sm"></div>
                <div className="w-2 md:w-1.5 h-6 md:h-4 bg-red-500 rounded-sm"></div>
                <div className="w-2 md:w-1.5 h-5 md:h-3 bg-teal-400 rounded-sm"></div>
                <div className="w-2 md:w-1.5 h-4 md:h-2 bg-blue-400 rounded-sm"></div>
              </div>
              <span className="ml-2 text-2xl md:text-xl font-bold text-gray-900">CoverBids</span>
              <span className="text-xs text-blue-600 ml-1"></span>
              </a>
            </div>
          
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Desktop navigation */}
            {user && (
              <div className="flex items-center gap-2 text-sm text-gray-500 order-3 w-full md:w-auto md:order-none">
                {user.app_metadata?.role === 'admin' && (
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                   
                  </span>
                )}
                {user.app_metadata?.role === 'insurer' && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    
                  </span>
                )}
               
              </div>
            )}
          
            <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-600 hover:text-gray-900">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/how-it-works" className="text-gray-600 hover:text-gray-900">How It Works</Link>
            <Link to="/register-insurer" className="text-gray-600 hover:text-gray-900 hidden md:block">Become an Insurer</Link>
            {user ? (
              <button
                onClick={() => setShowSupportModal(true)} 
                className={`${
                  user.app_metadata?.role === 'admin' && openTickets > 0 
                    ? 'text-red-600' 
                    : 'text-gray-600'
                } hover:text-gray-900`}
              >
                Support
                {user.app_metadata?.role === 'admin' && openTickets > 0 && (
                  <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                    {openTickets}
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={() => setShowSupportModal(true)}
                className="text-gray-600 hover:text-gray-900"
              >
                Support
              </button>
            )}
            {user && isAdmin && (
  <Link
    to="/admin/dashboard"
    className={`px-3 py-1.5 rounded-md ${
      showAdmin
        ? 'bg-red-600 text-white hover:bg-red-700'
        : 'bg-red-100 text-red-800 hover:bg-red-200'
    }`}
  >
    Admin Dashboard
  </Link>
)}

                {user && user.app_metadata?.role === 'insurer' && (
  <Link
    to="/insurer/dashboard"
    className={`px-3 py-1.5 rounded-md ${
      showDashboard
        ? 'bg-blue-600 text-white hover:bg-blue-700'
        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    }`}
  >
    Insurer Dashboard
  </Link>
)}
                {user ? (
                  <>
                    
                    <button
                      onClick={async () => {
                        try {
                          setIsLoading(true);
                          await signOut();
                          await refreshAuth();
                          navigate('/');
                        } catch (error) {
                          console.error('Error signing out:', error);
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      {isLoading ? 'Signing out...' : 'Sign Out'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Sign In
                  </button>
                )}
            </nav>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 border-t">
                {user && user.app_metadata?.role === 'admin' && (
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={closeMenu}
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Admin Dashboard
                  </Link>
                )}

                {user && user.app_metadata?.role === 'insurer' && (
                  <Link
                    to="/insurer/dashboard"
                    className="flex items-center px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={closeMenu}
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Insurer Dashboard
                  </Link>
                )}

                <Link
                  to="/"
                  className="flex items-center px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={closeMenu}
                >
                  <Home className="w-5 h-5 mr-2" />
                  Home
                </Link>

              <Link
                to="/how-it-works"
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                onClick={closeMenu}
              >
                How It Works
              </Link>

              <Link
                to="/register-insurer"
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                onClick={closeMenu}
              >
                Become an Insurer
              </Link>

              <button
                onClick={() => {
                  setShowSupportModal(true);
                  closeMenu();
                }}
                className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                Support
                {user?.app_metadata?.role === 'admin' && openTickets > 0 && (
                  <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                    {openTickets}
                  </span>
                )}
              </button>

              {user ? (
                <button
                  onClick={async () => {
                    closeMenu();
                    try {
                      setIsLoading(true);
                      await signOut();
                      navigate('/');
                      closeMenu();
                    } catch (error) {
                      console.error('Error signing out:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                >
                  {isLoading ? 'Signing out...' : 'Sign Out'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    closeMenu();
                  }}
                  className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}
      </header>
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <TicketModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </>
  );
}
